import humps from 'humps';
import {
  append,
  compose,
  evolve,
  init,
  join,
  map,
  path,
  pathOr,
  prop,
  split,
  tap,
  keys,
  pipe,
  isEmpty,
} from 'ramda';

import { GAME_ID_FROM_URL, MATKA_ID } from '@ezugi/constants';
import chip0 from '@ezugi/static/images/chip_0.png';
import chip1 from '@ezugi/static/images/chip_1.png';
import chip2 from '@ezugi/static/images/chip_2.png';
import chip3 from '@ezugi/static/images/chip_3.png';
import chip4 from '@ezugi/static/images/chip_4.png';
import chip5 from '@ezugi/static/images/chip_5.png';
import chip6 from '@ezugi/static/images/chip_6.png';
import type { TableChip } from '@ezugi/types';
import {
  isEvoWrapped,
  mapPropNames,
  parseJson,
  parsedQueryString,
  queryParams,
} from '@ezugi/utils';

import {
  ABTestingPayload,
  UIVersionType,
} from '../../../../../analytics/types';
import { LoadTableConfigResponse } from '../config/fetch';
import { pluckFeatureFlags } from '../config/utils';

const VITE_GAME_NAME = import.meta.env.VITE_GAME_NAME;

export function getAuthParams({
  operatorId,
  token,
}: {
  operatorId: string;
  token: string;
}) {
  const identifier = `${operatorId}${token}`;
  return { operatorId, token, identifier, ...parsedQueryString };
}

export const pluckLocale = prop('locale');

export const pluckUserData = compose(
  evolve({
    clientId: compose<any, any, any, any, any>(
      join('|'),
      append(queryParams.params.tableId),
      init,
      split('|')
    ),
    historyurl: (historyurl) => {
      if (!historyurl) return historyurl;
      try {
        // eslint-disable-next-line no-new
        new URL(historyurl);
        return historyurl;
      } catch (_error) {
        return `https://${historyurl}`;
      }
    },
  }),
  mapPropNames(humps.camelize),
  prop('userData')
);

export const pluckWidgets = compose(
  tap((widgets: any) => {
    widgets.responsibleGaming = widgets.responsibleGaming || !!parsedQueryString.responsibleGamingUrl;
  }),
  mapPropNames(humps.camelize),
  path([ 'config', 'widgets' ])
);

export const pluckGroups = compose(
  map(evolve({ tables: map(Number) })),
  prop('groups')
);

export const pluckHome = compose(
  mapPropNames(humps.camelize),
  path([ 'config', 'home' ])
);

export const pluckChat = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'chat' ])
);

export const pluckGeneral = compose<any, any, any, any>(
  mapPropNames(humps.camelize),
  map(parseJson),
  path([ 'config', 'general' ])
);

export const pluckGameResults = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'game_results' ])
);

export const pluckWinnerList = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'winner_list' ])
);

export const pluckTable = compose<any, any>(
  path([ 'tables', queryParams.params.tableId ])
);

export const pluckTables = prop('tables');

export const pluckCashier = compose(
  mapPropNames(humps.camelize),
  path([ 'config', 'cashier' ])
);

export const pluckAmountDisplay = compose(
  mapPropNames(humps.camelize),
  path([ 'config', 'amount_display' ])
);

export const pluckGameTypeData = compose<any, any, any, any>(
  mapPropNames(humps.camelize),
  map(parseJson),
  path([ 'config', VITE_GAME_NAME! ])
);

export const pluckBjMaximumSeatsPerPLayer = compose(
  pathOr(2, [ 'config', 'blackjack', 'max_no_of_seats_per_player' ])
);

export const pluckFilters = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'filters' ])
);

export const pluckBetsConfig = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'bets' ])
);

const evoChips = [
  chip0,
  chip1,
  chip2,
  chip3,
  chip4,
  chip5,
  chip6,
];
export const configureChips = (tableChips: TableChip[]): TableChip[] => {
  const isWrapped = isEvoWrapped();

  const chips = tableChips?.slice(0, 7) ?? [];

  if (isWrapped) {
    return chips.map((chip, index) => ({
      ...chip,
      img: evoChips[index] || chip.img,
      color: '#FFF',
    }));
  }

  return chips.map((chip) => ({
    ...chip,
    color: '#000',
  }));
};
export const pluckAutoplay = compose(
  mapPropNames(humps.camelize),
  pathOr({}, [ 'config', 'autoplay' ])
);

export function getInitialChip({
  chips,
  minBet,
}: {
  chips: TableChip[];
  minBet: number;
}) {
  const configuredChips = configureChips(chips);
  const minLimitChip = configuredChips.find((chip) => chip.value >= minBet);
  const defaultSelectedChip = configuredChips.find((chip) => chip.default);

  return defaultSelectedChip || minLimitChip || chips[0];
}

export const pluckLanguage = prop('language');

export const pluckExternalGameTypes = pipe(
  pathOr({}, [ 'game_types_from_providers' ]),
  keys,
  map(Number)
);

export const pluckComplianceConfig = path([ 'config', 'compliance' ]);

export const pluckGameServerConfig = path([ 'config', 'game-server' ]);

const getABTestingUIVersionParam = (data: LoadTableConfigResponse['result']): { uiVersion?: UIVersionType } => {
  const featureFlags = pluckFeatureFlags(data) as any;
  const newRedesignEnabled = featureFlags?.newRedesignEnabled ?? null;
  if (newRedesignEnabled === null) {
    return {};
  }
  const uiVersion = newRedesignEnabled ? '4.0' : '3.0';
  return { uiVersion };
};

export const getMatkaABTesting = (
  { config }: LoadTableConfigResponse['result']
) => {
  if (GAME_ID_FROM_URL !== MATKA_ID) return {};

  return {
    // the 2 AB testings are in conflict, and even if `auto_place_bet` is true, when `hide_numbers_grid_until_bet_placed` is true,
    // player is on the hide_numbers_grid_until_bet_placed version and not on the auto_place_bet version
    matkaAutoPlaceBet: config.matka.auto_place_bet && !config.matka.hide_numbers_grid_until_bet_placed ? 1 : 0,
    matkaHideNumbersGridUntilBetPlaced: config.matka.hide_numbers_grid_until_bet_placed ? 1 : 0,
  };
};

export const getABTestingPayload = (
  data: LoadTableConfigResponse['result']
): ABTestingPayload => {
  if (isEmpty(data)) {
    return {};
  }
  return {
    ...getABTestingUIVersionParam(data),
    ...getMatkaABTesting(data),
  };
};
